// src/lib/log.ts
import type { NextApiRequest, NextApiResponse } from 'next';

// ---- shape used across the app ----
type Channel = {
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  debug: (...args: any[]) => void;
};

export type LogLike = {
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  debug: (...args: any[]) => void;
  // channels used widely in the codebase:
  api: Channel;
  db: Channel;
  auth: Channel;
  permissions: Channel;
  revalidate: Channel;
  middleware: Channel;
  // context management
  getContext: () => any[];
  // convenience
  child?: (bindings: Record<string, unknown>) => LogLike;
};

// simple console-backed logger with the same surface
function makeLogger(prefix = 'app'): LogLike {
  const logFn =
    (level: 'info' | 'warn' | 'error' | 'debug', channel?: string) =>
    (...args: any[]) => {
      let msg = `[${prefix}${channel ? `:${channel}` : ''}]`;
      let data: any = {};

      if (args.length === 1) {
        // Single argument - could be string or object
        const arg = args[0];
        if (typeof arg === 'string') {
          data = { msg: arg };
        } else {
          data = arg || {};
          if (!data.msg) data.msg = 'log';
        }
      } else if (args.length >= 2) {
        // Multiple arguments - first is message, rest are data
        const message = args[0];
        const rest = args.slice(1);
        data = { msg: message };
        if (rest.length === 1 && typeof rest[0] === 'object') {
          Object.assign(data, rest[0]);
        } else if (rest.length > 0) {
          data.data = rest;
        }
      }

      // eslint-disable-next-line no-console
      (console as any)[level] ? (console as any)[level](msg, data) : console.log(msg, data);
    };

  // Create channel objects with methods
  const makeChannel = (channelName: string) => ({
    info: logFn('info', channelName),
    warn: logFn('warn', channelName),
    error: logFn('error', channelName),
    debug: logFn('debug', channelName),
  });

  const base: LogLike = {
    info: logFn('info'),
    warn: logFn('warn'),
    error: logFn('error'),
    debug: logFn('debug'),
    api: makeChannel('api'),
    db: makeChannel('db'),
    auth: makeChannel('auth'),
    permissions: makeChannel('permissions'),
    revalidate: makeChannel('revalidate'),
    middleware: makeChannel('middleware'),
    getContext: () => [],
  };

  base.child = (bindings) => makeLogger(prefix + ':' + Object.values(bindings).join(':'));
  return base;
}

// default + named export (so both import styles work)
const log = makeLogger();
export default log;
export { log };
export { log as logger };

// per-request logger helpers used all over the API routes
export function attachLogger(req: NextApiRequest, _res?: NextApiResponse): void {
  (req as any).logger = (req as any).logger ?? log.child?.({ reqId: (req as any).requestId || Date.now() }) ?? log;
}

export function getRequestLogger(req?: NextApiRequest): LogLike {
  return (req as any)?.logger ?? log;
}

export function hasLogger(req?: NextApiRequest): boolean {
  return Boolean((req as any)?.logger);
}

/**
 * Get the base/global logger instance
 */
export function getLogger(name?: string): LogLike {
  if (name && log.child) {
    return log.child({ component: name });
  }
  return log;
}

/**
 * Create a safe logger wrapper that never throws, even if the underlying logger is undefined
 */
export function safeLogger(logger?: LogLike | null): LogLike {
  const noop = () => {};

  return {
    info: (...args: any[]) => {
      try {
        logger?.info?.(...args);
      } catch (error) {
        // Fallback to console if logger fails
        console.info('[SAFE_LOG]', ...args);
      }
    },
    warn: (...args: any[]) => {
      try {
        logger?.warn?.(...args);
      } catch (error) {
        console.warn('[SAFE_LOG]', ...args);
      }
    },
    error: (...args: any[]) => {
      try {
        logger?.error?.(...args);
      } catch (error) {
        console.error('[SAFE_LOG]', ...args);
      }
    },
    debug: (...args: any[]) => {
      try {
        logger?.debug?.(...args);
      } catch (error) {
        console.debug('[SAFE_LOG]', ...args);
      }
    },
    api: {
      info: (...args: any[]) => {
        try {
          logger?.api?.info?.(...args);
        } catch (error) {
          console.info('[SAFE_LOG:API]', ...args);
        }
      },
      warn: (...args: any[]) => {
        try {
          logger?.api?.warn?.(...args);
        } catch (error) {
          console.warn('[SAFE_LOG:API]', ...args);
        }
      },
      error: (...args: any[]) => {
        try {
          logger?.api?.error?.(...args);
        } catch (error) {
          console.error('[SAFE_LOG:API]', ...args);
        }
      },
      debug: (...args: any[]) => {
        try {
          logger?.api?.debug?.(...args);
        } catch (error) {
          console.debug('[SAFE_LOG:API]', ...args);
        }
      },
    },
    db: {
      info: (...args: any[]) => {
        try {
          logger?.db?.info?.(...args);
        } catch (error) {
          console.info('[SAFE_LOG:DB]', ...args);
        }
      },
      warn: (...args: any[]) => {
        try {
          logger?.db?.warn?.(...args);
        } catch (error) {
          console.warn('[SAFE_LOG:DB]', ...args);
        }
      },
      error: (...args: any[]) => {
        try {
          logger?.db?.error?.(...args);
        } catch (error) {
          console.error('[SAFE_LOG:DB]', ...args);
        }
      },
      debug: (...args: any[]) => {
        try {
          logger?.db?.debug?.(...args);
        } catch (error) {
          console.debug('[SAFE_LOG:DB]', ...args);
        }
      },
    },
    auth: {
      info: (...args: any[]) => {
        try {
          logger?.auth?.info?.(...args);
        } catch (error) {
          console.info('[SAFE_LOG:AUTH]', ...args);
        }
      },
      warn: (...args: any[]) => {
        try {
          logger?.auth?.warn?.(...args);
        } catch (error) {
          console.warn('[SAFE_LOG:AUTH]', ...args);
        }
      },
      error: (...args: any[]) => {
        try {
          logger?.auth?.error?.(...args);
        } catch (error) {
          console.error('[SAFE_LOG:AUTH]', ...args);
        }
      },
      debug: (...args: any[]) => {
        try {
          logger?.auth?.debug?.(...args);
        } catch (error) {
          console.debug('[SAFE_LOG:AUTH]', ...args);
        }
      },
    },
    permissions: {
      info: (...args: any[]) => {
        try {
          logger?.permissions?.info?.(...args);
        } catch (error) {
          console.info('[SAFE_LOG:PERMISSIONS]', ...args);
        }
      },
      warn: (...args: any[]) => {
        try {
          logger?.permissions?.warn?.(...args);
        } catch (error) {
          console.warn('[SAFE_LOG:PERMISSIONS]', ...args);
        }
      },
      error: (...args: any[]) => {
        try {
          logger?.permissions?.error?.(...args);
        } catch (error) {
          console.error('[SAFE_LOG:PERMISSIONS]', ...args);
        }
      },
      debug: (...args: any[]) => {
        try {
          logger?.permissions?.debug?.(...args);
        } catch (error) {
          console.debug('[SAFE_LOG:PERMISSIONS]', ...args);
        }
      },
    },
    revalidate: {
      info: (...args: any[]) => {
        try {
          logger?.revalidate?.info?.(...args);
        } catch (error) {
          console.info('[SAFE_LOG:REVALIDATE]', ...args);
        }
      },
      warn: (...args: any[]) => {
        try {
          logger?.revalidate?.warn?.(...args);
        } catch (error) {
          console.warn('[SAFE_LOG:REVALIDATE]', ...args);
        }
      },
      error: (...args: any[]) => {
        try {
          logger?.revalidate?.error?.(...args);
        } catch (error) {
          console.error('[SAFE_LOG:REVALIDATE]', ...args);
        }
      },
      debug: (...args: any[]) => {
        try {
          logger?.revalidate?.debug?.(...args);
        } catch (error) {
          console.debug('[SAFE_LOG:REVALIDATE]', ...args);
        }
      },
    },
    middleware: {
      info: (...args: any[]) => {
        try {
          logger?.middleware?.info?.(...args);
        } catch (error) {
          console.info('[SAFE_LOG:MIDDLEWARE]', ...args);
        }
      },
      warn: (...args: any[]) => {
        try {
          logger?.middleware?.warn?.(...args);
        } catch (error) {
          console.warn('[SAFE_LOG:MIDDLEWARE]', ...args);
        }
      },
      error: (...args: any[]) => {
        try {
          logger?.middleware?.error?.(...args);
        } catch (error) {
          console.error('[SAFE_LOG:MIDDLEWARE]', ...args);
        }
      },
      debug: (...args: any[]) => {
        try {
          logger?.middleware?.debug?.(...args);
        } catch (error) {
          console.debug('[SAFE_LOG:MIDDLEWARE]', ...args);
        }
      },
    },
    getContext: () => logger?.getContext?.() ?? [],
  };
}

// small convenience wrappers referenced in many files
export function logApiInfo(req: NextApiRequest, ...args: any[]) {
  getRequestLogger(req).api.info(...args);
}
export function logApiWarn(req: NextApiRequest, ...args: any[]) {
  getRequestLogger(req).api.warn(...args);
}
export function logApiError(req: NextApiRequest, ...args: any[]) {
  getRequestLogger(req).api.error(...args);
}